import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthenticationService } from '@app/core';
import { ToastService } from '@projects/shared/src/app/services/toast/toast.service';
import { MFAFlags } from '@projects/shared/src/app/enums/MFAFlags';
import { MFATypes } from '@projects/agent/src/app/models/userprofile';
import { CodeVerificationResponse, SendVerificationCodeResponse } from '@app/core/types/user';
import { formatTimeLeft } from '@projects/shared/src/app/utils/helper';

@Component({
  selector: 'app-email-verification',
  templateUrl: './email-verification-updated.component.html',
  styleUrl: './email-verification.component.scss',
})
export class EmailVerificationComponent implements OnInit {
  @Input() userId?: string | null;
  @Input() email?: string | null;
  @Input() isLoginFlow = true;
  @Input() imagePath = '';

  @Output() emailVerified = new EventEmitter<boolean>();

  code = '';
  emailCount = 0;
  timeLeft = 0;
  private timerInterval: any;
  isEmailSent = false;
  isError = false;
  verified: any;
  constructor(
    private authService: AuthenticationService,
    private toastService: ToastService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    if (this.isLoginFlow) {
      this.userId = this.route.snapshot.queryParamMap.get('id');
      this.email = this.route.snapshot.queryParamMap.get('email');
      this.verified = this.route.snapshot.queryParamMap.get('verified');

      this.sendCodeEmail();
    }
  }

  get displayTimeLeft(): string {
    return formatTimeLeft(this.timeLeft);
  }

  /**
   * Sends verification code to an email address
   */
  sendCodeEmail() {
    this.validateUserId();

    if (this.emailCount >= 3) {
      this.toastService.warning('You have reached the limit to send an email. Please try again later');
      return;
    }

    this.isEmailSent = false;
    this.authService.sendMfaVerificationCode({ UserId: this.userId, MfaChannel: MFATypes.Email }).subscribe({
      next: (response: SendVerificationCodeResponse) => {
        this.toastService.success('Please check your email for the confirmation code.');
        this.isEmailSent = true;
        this.emailCount++;
        this.timeLeft = response.ExpireInSeconds;
        this.startResendTimer();
      },
      error: (err: any) => {
        this.isEmailSent = false;
        this.toastService.error('Failed to send the email verification code. Please try again later.');
      },
    });
  }

  /**
   * Verifies code for Email verification
   * @param code
   * @returns
   */
  verifyCode(code: any) {
    this.validateUserId();

    if (code === undefined || code === '') {
      this.toastService.error("Sorry, we don't recognize this verification code.");
      this.isError = true;
      this.code = '';
      return;
    }

    const codeVerification = {
      UserId: this.userId || '',
      Code: code,
      IsLoginFlow: this.isLoginFlow,
      MfaChannel: MFATypes.Email,
    };

    this.authService.verifyCode(codeVerification).subscribe((response: CodeVerificationResponse) => {
      if (response?.Success) {
        this.toastService.success('Email verified successfully.');
        this.code = '';

        if (this.isLoginFlow) {
          this.authService.configureMFAAuthData(true, MFAFlags.Email);
          this.router.navigateByUrl('login');
        } else {
          this.emailVerified.emit(true);
        }
      } else {
        this.authService.handleVerificationError(response.Error);

        if (this.isLoginFlow) this.authService.configureMFAAuthData(false, MFAFlags.Email);
      }
    });
  }

  /**
   * Back to home
   */
  onBack() {
    this.authService.onLogOut();
  }

  //#region Private methods

  private startResendTimer() {
    if (this.timeLeft <= 0) return;

    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
    this.timerInterval = setInterval(() => {
      if (this.timeLeft > 0) {
        this.timeLeft--;
      } else {
        clearInterval(this.timerInterval);
      }
    }, 1000);
  }

  private validateUserId(): void {
    if (!this.userId) {
      if (this.isLoginFlow) {
        this.toastService.error('User is not logged in.');
        this.router.navigateByUrl('/login');
      } else {
        this.toastService.error('User is not provided.');
        this.emailVerified.emit(false);
      }

      return;
    }
  }

  //#endregion
}
